import type { PloscicaPregleda, TockaPregleda } from '@koda/shared';
import type { JSX } from 'react';

import { evri } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { useTelefon } from '../lib/telefon';
import { celicaDelov, naTipko } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';

/** Mere grafa po zasnovi: splet 412 × 250, telefon 342 × 200. */
export interface MereStolpcev {
  sirina: number;
  visina: number;
  levo: number;
  desno: number;
  vrh: number;
  nicla: number;
  stolpec: number;
}
export const STOLPCI_SPLET: MereStolpcev = { sirina: 412, visina: 250, levo: 44, desno: 404, vrh: 16, nicla: 218, stolpec: 16 };
export const STOLPCI_TELEFON: MereStolpcev = { sirina: 342, visina: 200, levo: 40, desno: 334, vrh: 16, nicla: 168, stolpec: 15 };
/** Podrobno: kartica ~2/3 širine (zasnova »Podrobno – splet«), enaka višina kot ~1/3. */
export const STOLPCI_SIROKO: MereStolpcev = { sirina: 850, visina: 250, levo: 44, desno: 842, vrh: 16, nicla: 218, stolpec: 22 };

/** Lep korak osi (1, 2, 2,5 ali 5 × 10ⁿ), da je razdelkov največ pet. */
export function korakOsi(najvec: number): number {
  if (!(najvec > 0)) return 1;
  const grob = najvec / 5;
  const red = 10 ** Math.floor(Math.log10(grob));
  for (const f of [1, 2, 2.5, 5, 10]) if (f * red >= grob) return f * red;
  return 10 * red;
}

/** Oznaka osi v tisočih: »50 k«, »2,5 k«. */
export function tisoc(v: number): string {
  return `${new Intl.NumberFormat('sl-SI', { maximumFractionDigits: 1 }).format(v / 1000)} k`;
}

export const zaobljen = (x: number, y: number, w: number, nicla: number): string => {
  // Stolpec z zaobljenim vrhom (polmer 4), kot v zasnovi.
  const r = Math.min(4, Math.abs(nicla - y) / 2, w / 2);
  const f = (v: number): string => v.toFixed(1);
  return `M${f(x)},${f(nicla)} L${f(x)},${f(y + r)} Q${f(x)},${f(y)} ${f(x + r)},${f(y)} L${f(x + w - r)},${f(y)} Q${f(x + w)},${f(y)} ${f(x + w)},${f(y + r)} L${f(x + w)},${f(nicla)} Z`;
};

interface Lastnosti {
  ploscica: PloscicaPregleda;
  leto: number;
  primerjalno: number | null;
  naCelico: (celica: Celica) => void;
  /** Mere na spletu; privzeto kartica ~1/3 širine (Pregled). */
  mere?: MereStolpcev;
}

/**
 * Prihodki po mesecih (točka 7): samo meseci s podatki tekočega leta, stolpca
 * obeh let (lani svetel, letos temen), os z vrednostmi in oznaka ob najvišjem
 * letošnjem stolpcu. Graf zapolni kartico. Stolpec odpre knjižbe meseca.
 */
export function GrafPrihodkov({ ploscica: p, leto, primerjalno, naCelico, mere = STOLPCI_SPLET }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const m = useTelefon() ? STOLPCI_TELEFON : mere;
  const t = p.poMesecih.t;
  const meseci = t.length;
  // Lani samo isti meseci kot letos.
  const t1 = primerjalno === null ? [] : p.poMesecih.t1.filter((x) => x.mesec <= (t[meseci - 1]?.mesec ?? 0));
  const vse = [...t, ...t1].map((x) => Number(x.vrednost));
  const korak = korakOsi(Math.max(0, ...vse) - Math.min(0, ...vse));
  const najvec = Math.max(korak, Math.ceil(Math.max(0, ...vse) / korak) * korak);
  // Negativen mesec (npr. dobropisi) gre pod ničelno črto; os se podaljša navzdol.
  const najmanj = Math.floor(Math.min(0, ...vse) / korak) * korak;
  const y = (v: number): number => m.vrh + ((najvec - v) / (najvec - najmanj)) * (m.nicla - m.vrh);
  const razdelki = Array.from({ length: Math.round((najvec - najmanj) / korak) + 1 }, (_, i) => najmanj + i * korak);
  const sirinaMeseca = (m.desno - m.levo) / Math.max(meseci, 1);
  const sredina = (i: number): number => m.levo + (i + 0.5) * sirinaMeseca;
  const vrhLetos = t.reduce((naj, x, i) => (Number(x.vrednost) > Number(t[naj]?.vrednost ?? '-Infinity') ? i : naj), 0);

  const stolpec = (tc: TockaPregleda, i: number, l: number, lani: boolean): JSX.Element => {
    const x = lani ? sredina(i) - 1 - m.stolpec : sredina(i) + 1;
    const naslov = `${b.meseci[tc.mesec - 1] ?? String(tc.mesec)} ${String(l)}`;
    const v = Number(tc.vrednost);
    const negativen = v < 0;
    return (
      <path
        key={`${String(l)}-${String(tc.mesec)}`}
        className={`kz-stolpec${negativen ? ' izguba' : ''}`}
        d={
          negativen
            ? `M${x.toFixed(1)},${y(0).toFixed(1)} H${(x + m.stolpec).toFixed(1)} V${y(v).toFixed(1)} H${x.toFixed(1)} Z`
            : zaobljen(x, y(v), m.stolpec, y(0))
        }
        fill={negativen ? '#a4262c' : lani ? '#86b6ef' : '#256abf'}
        role="button"
        tabIndex={0}
        aria-label={`${naslov}: ${evri(tc.vrednost)}`}
        onClick={() => {
          naCelico(celicaDelov(p.naziv, naslov, tc.vrednost, tc.deli));
        }}
        onKeyDown={naTipko(() => {
          naCelico(celicaDelov(p.naziv, naslov, tc.vrednost, tc.deli));
        })}
      >
        <title>{`${naslov}: ${evri(tc.vrednost)}`}</title>
      </path>
    );
  };

  return (
    <section className="kz-karta kz-graf-prihodkov" aria-label={b.pk.grafPrihodkov}>
      <div className="kz-karta-glava">
        <div className="kz-karta-naslov">
          <h2>{b.pk.grafPrihodkov}</h2>
          <div className="kz-karta-podnaslov">{p.deli.map((d) => d.naziv).join(', ')}</div>
        </div>
        <div className="kz-legenda">
          <span>
            <span className="kz-kvadrat" style={{ background: '#256abf' }} />
            {leto}
          </span>
          {primerjalno !== null && (
            <span>
              <span className="kz-kvadrat" style={{ background: '#86b6ef' }} />
              {primerjalno}
            </span>
          )}
        </div>
      </div>
      <svg
        className="kz-stolpci"
        viewBox={`0 0 ${String(m.sirina)} ${String(m.visina)}`}
        role="img"
        aria-label={primerjalno === null ? `${b.pk.grafPrihodkov}, ${String(leto)}` : `${b.pk.grafPrihodkov}, ${String(leto)} ${b.pk.in} ${String(primerjalno)}`}
      >
        {razdelki.map((v) => (
          <g key={v}>
            <line x1={m.levo} x2={m.desno} y1={y(v).toFixed(1)} y2={y(v).toFixed(1)} stroke={v === 0 ? '#cfccc4' : '#e9e7e1'} strokeWidth="1" />
            <text x={m.levo - 8} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="11" fill="#6f6c65">
              {tisoc(v)}
            </text>
          </g>
        ))}
        {t.map((tc, i) => {
          const lani = t1.find((x) => x.mesec === tc.mesec);
          return (
            <g key={tc.mesec}>
              {lani !== undefined && primerjalno !== null && stolpec(lani, i, primerjalno, true)}
              {stolpec(tc, i, leto, false)}
              <text x={sredina(i).toFixed(1)} y={m.nicla + 22} textAnchor="middle" fontSize="12" fill="#5d5b55">
                {(b.meseci[tc.mesec - 1] ?? String(tc.mesec)).slice(0, 3)}
              </text>
            </g>
          );
        })}
        {t[vrhLetos] !== undefined && (
          <text
            x={(sredina(vrhLetos) + 1 + m.stolpec / 2).toFixed(1)}
            y={(y(Number(t[vrhLetos].vrednost)) - 8).toFixed(1)}
            textAnchor="middle"
            fontSize="11"
            fontWeight="600"
            fill="#1c1b19"
          >
            {tisoc(Math.round(Number(t[vrhLetos].vrednost) / 1000) * 1000)}
          </text>
        )}
      </svg>
    </section>
  );
}
